'use client';

import { useState, type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Loading03Icon,
  Tick01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { pythonPipelineStageSchema } from '@terra/shared-chat-trace';
import { z } from 'zod';
import { MarkdownPreview } from '../../markdown-preview';
import type { TraceToolCall } from '../../chat/trace-view/types';

const pipelineSchema = z.object({
  stage: pythonPipelineStageSchema.optional(),
  resolvedSecrets: z.boolean().optional(),
  guardrailBlock: z.boolean().optional(),
  feedback: z.string().optional(),
  durationMs: z.number().optional(),
  guardrails: z
    .object({
      ok: z.boolean(),
      feedback: z.string().optional(),
      reasoning: z.string().optional(),
      response: z.string().optional(),
    })
    .optional(),
});

export function PythonPipelineTrace({
  toolCall,
  children,
}: {
  toolCall: TraceToolCall;
  children: ReactNode;
}) {
  const t = useTranslations('designSystem.chat.pythonPipeline');
  const [open, setOpen] = useState(false);
  const [guardrailsOpen, setGuardrailsOpen] = useState(false);
  const parsed = pipelineSchema.safeParse(toolCall.metadata?.pythonPipeline);
  const pipeline = parsed.success ? parsed.data : undefined;
  const failed = toolCall.status === 'error' || Boolean(toolCall.error);
  const running = !failed && toolCall.status !== 'completed';
  const blocked =
    pipeline?.guardrailBlock ||
    pipeline?.guardrails?.ok === false ||
    toolCall.metadata?.errorType === 'dynamic_guardrail' ||
    toolCall.metadata?.errorType === 'static_guardrail';
  const expandable = !running && (!failed || blocked);
  const durationMs = pipeline?.durationMs ?? toolCall.timing?.durationMs;
  const label = running
    ? pipeline?.stage === 'executing'
      ? ''
      : pipeline?.stage === 'enforcing-guardrails'
        ? t('enforcing')
        : t('resolving')
    : failed
      ? t('failed')
      : durationMs === undefined
        ? t('completed')
        : t('completedIn', { seconds: (durationMs / 1000).toFixed(1) });
  const explanation = (
    <VStack alignItems="stretch" gap={2}>
      {pipeline?.guardrails?.reasoning ? (
        <MarkdownPreview source={pipeline.guardrails.reasoning} fontSize={12} />
      ) : null}
      {pipeline?.guardrails?.response ? (
        <MarkdownPreview source={pipeline.guardrails.response} fontSize={12} />
      ) : null}
      {!pipeline?.guardrails?.response &&
      (pipeline?.guardrails?.feedback ||
        pipeline?.feedback ||
        (blocked && toolCall.output)) ? (
        <MarkdownPreview
          source={
            pipeline?.guardrails?.feedback ||
            pipeline?.feedback ||
            toolCall.output ||
            ''
          }
          fontSize={12}
        />
      ) : null}
    </VStack>
  );
  const header = (
    <>
      <Box flexShrink={0}>{t('title')}</Box>
      {label ? (
        <>
          <Box aria-hidden="true" flexShrink={0}>
            •
          </Box>
          <Box>{label}</Box>
        </>
      ) : null}
      {running ? (
        <Box
          display="flex"
          flexShrink={0}
          animation="spin 1s linear infinite"
          role="status"
          aria-label={t('loading')}
        >
          <Loading03Icon size={12} />
        </Box>
      ) : expandable ? (
        open ? (
          <ArrowDown01Icon size={12} />
        ) : (
          <ArrowRight01Icon size={12} />
        )
      ) : null}
    </>
  );

  return (
    <VStack alignItems="stretch" gap={2} fontSize="xs" color="fg.muted">
      {expandable ? (
        <HStack
          as="button"
          gap={1.5}
          py={1}
          cursor="pointer"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {header}
        </HStack>
      ) : (
        <HStack gap={1.5} py={1}>
          {header}
        </HStack>
      )}
      {open && expandable ? (
        failed ? (
          explanation
        ) : (
          <VStack alignItems="stretch" gap={2}>
            {pipeline?.resolvedSecrets ? (
              <HStack gap={1.5}>
                {t('resolved')}
                <Tick01Icon size={12} />
              </HStack>
            ) : null}
            {pipeline?.guardrails ? (
              <Box>
                <HStack
                  as="button"
                  gap={1.5}
                  cursor="pointer"
                  aria-expanded={guardrailsOpen}
                  onClick={() => setGuardrailsOpen(!guardrailsOpen)}
                  css={{
                    '&:hover > [data-guardrails-chevron], &:focus-visible > [data-guardrails-chevron]':
                      {
                        opacity: 1,
                      },
                  }}
                >
                  <Box>{t('enforced')}</Box>
                  <Box aria-hidden="true" flexShrink={0}>
                    •
                  </Box>
                  <Box>{t('completed')}</Box>
                  <Box
                    data-guardrails-chevron
                    aria-hidden="true"
                    display="flex"
                    flexShrink={0}
                    opacity={0}
                  >
                    {guardrailsOpen ? (
                      <ArrowDown01Icon size={12} />
                    ) : (
                      <ArrowRight01Icon size={12} />
                    )}
                  </Box>
                </HStack>
                {guardrailsOpen ? <Box pt={2}>{explanation}</Box> : null}
              </Box>
            ) : null}
            {children}
          </VStack>
        )
      ) : null}
    </VStack>
  );
}
